<script setup lang="ts">
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {DialogContent, DialogTitle} from "@/core/components/ui/dialog";
import {AutoCompleteSearchbar} from "@/auto-complete-searchbar/components/searchbar";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import DatePicker from "@/core/components/date-picker/DatePicker.vue";
import {computed, onMounted, ref, watch} from "vue";
import {Act} from "@/backing/interfaces/backing";
import {Contest, ContestTemplate} from "@/contest/interfaces";
import {CalendarDate, DateValue, getLocalTimeZone, now} from "@internationalized/date";
import {SearchBarAutoCompleteItem} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";
import ImageFile from "@/core/interfaces/laboratory/ImageFile";
import {FileManagerFile} from "@/core/interfaces/FileManagerFile";
import useSwr from "@/core/composables/swr/useSwr";
import {cacheKey} from "@/lib/cache";
import {actApi, contestApi, filemanagerFileApi} from "@/container";
import SalesConfigService from "@/contest/services/SalesConfigService";
import {currencyze} from "@/core/filters/Currencyze";

const strategies = [
    "SalesQuantityContestStrategy",
    "SalesQuantityObjectiveContest"
];

const props = defineProps<
    { edit: true; contestId: number; openState?: boolean; template?: ContestTemplate }
    | { edit?: false; contestId?: number; openState?: boolean; template?: ContestTemplate }
>();

const emits = defineEmits(['on-refresh', 'update:open']);

const loading = ref(false);
const fetching = ref(false);
const selectedActs = ref<Act[]>([]);
const configuredPrestations = ref<string[]>([]);
const contest = ref<Contest>();
const quantityObjective = ref<number>(0);
const title = ref<string>("");
const description = ref<string>("");
const startDate = ref<DateValue>();
const endDate = ref<DateValue>();
const items = ref<SearchBarAutoCompleteItem[]>([]);
const config = ref<any>({});
const contestFile = ref<ImageFile | FileManagerFile | null>();

const {data: acts} = useSwr(() => cacheKey("/acts"), () => actApi().index());

const isMandatoryFieldFilled = computed<boolean>(() => {
    return !(!startDate.value || !endDate.value || !title.value?.length || !description.value?.length);

});

onMounted(async () => {
    if (props.contestId) {
        await fetchContest(props.contestId);
    }
});

watch(() => props.contestId, async (contestId) => {
    if (contestId) {
        await fetchContest(contestId);
    }
});

/**
 * On modal close, reset the form.
 */
watch(() => props.openState, (newValue) => {
    if (!newValue && !props.contestId) {
        startDate.value = undefined;
        endDate.value = undefined;
        title.value = "";
        description.value = "";
        items.value = [];
        contestFile.value = null;
        config.value = {};
        selectedActs.value = [];
        configuredPrestations.value = [];
        quantityObjective.value = 0;
    }
});

watch(() => props.template, (newValue) => {
    if (newValue) {
        title.value = newValue.name;
        description.value = newValue.description;
        configuredPrestations.value = newValue.config?.prestations ?? [];
        contestFile.value = newValue.file;

        if (newValue.config?.objective?.target) {
            quantityObjective.value = newValue.config.objective.target;
        }
    }
});

/**
 * Check the configured acts once both the configuration and the act list are loaded,
 * whichever arrives last; a later refresh of the act list keeps the current choice.
 */
watch([acts, configuredPrestations], ([list, keys], [previousList, previousKeys]) => {
    if (list && (!previousList || keys !== previousKeys)) {
        selectedActs.value = SalesConfigService().actsFromPrestations(keys, list);
    }
});

/**
 * Fetch contest information and initialize form values based on the contest data.
 */
const fetchContest = async (contestId: number) => {
    fetching.value = true;
    const response = await contestApi().read(contestId);
    contest.value = response.data;
    config.value = response.meta.config;
    fetching.value = false;

    title.value = contest.value.name;
    description.value = contest.value.description;
    const start = new Date(contest.value.start_date);
    const end = new Date(contest.value.end_date);
    startDate.value = new CalendarDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
    endDate.value = new CalendarDate(end.getFullYear(), end.getMonth() + 1, end.getDate());
    contestFile.value = contest.value.file;

    if (config.value) {
        configuredPrestations.value = config.value?.prestations ?? [];
        items.value = SalesConfigService().getSearchBarAutoCompleteItemsFromConfig(config.value);
    }

    if (contest.value?.objective?.target) {
        quantityObjective.value = contest.value.objective.target;
    }
};

/**
 * Handle submit of the contest creation form.
 */
const onSubmit = () => {
    if (!isMandatoryFieldFilled.value) {
        return;
    }

    loading.value = true;

    const formattedItems = SalesConfigService().formatSearchbarAutoCompleteData(items.value);
    config.value = SalesConfigService().formatConfig(formattedItems, quantityObjective.value, selectedActs.value, acts.value ?? [], configuredPrestations.value);

    const data = {
        name: title.value,
        description: description.value,
        start_date: startDate.value?.toString(),
        end_date: endDate.value?.toString(),
        file_id: contestFile.value?.id,
        is_published: true,
        strategy: quantityObjective.value === 0 ? strategies[0] : strategies[1],
        config: config.value,
    };

    if (props.edit) {
        contestApi().update(props.contestId, data)
            .then(() => emits('on-refresh'))
            .finally(() => {
                loading.value = false;
                emits('update:open', false);
            });
    } else {
        contestApi().create(data)
            .then(() => emits('on-refresh'))
            .finally(() => {
                loading.value = false;
                emits('update:open', false);
            });
    }
};


/**
 * Open the file upload dialog
 */
const openFileUpload = () => {
    document.getElementById('input-file')?.click();
};

/**
 * Handle the file upload
 *
 * @param {File} file
 */
const handleFileUpload = (file: File | File[]) => {
    filemanagerFileApi().upload(Array.isArray(file) ? file[0] : file)
        .then(file => contestFile.value = file);
};
</script>

<template>
  <DialogContent class="max-h-[90%] min-w-[80%] flex flex-col items-start overflow-y-auto">
    <DialogTitle class="bg-white">
      <h2 class="text-lg pb-2">
        Configurez votre challenge
      </h2>
      <span class="text-sm font-normal">
        Configurez les paramètres du challenge de ventes, tous les éléments sélectionnés sont pris en compte
        (ignorez les éléments que vous ne voulez pas incorporer dans le challenge).
      </span>
    </DialogTitle>
    <base-flex-spinner v-if="fetching" />
    <div v-else class="w-full">
      <div class="pb-4">
        <span>Sur quel code acte souhaitez-vous travailler ?</span>
        <Select v-model:model-value="selectedActs" multiple>
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner un code acte" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="act in (acts ?? [])" :key="act.id"
              :value="act"
            >
              <span class="text-gray-500">{{ act.code }}</span> - {{ act.name }}
              <template v-if="SalesConfigService().hasPricedVariants(act, acts ?? [])">
                ({{ currencyze(act.price) }})
              </template>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div class="pb-4">
        <span>Sélectionnez les produits du challenge</span>
        <AutoCompleteSearchbar
          v-model:selected-items="items"
        />
      </div>
      <div class="pb-4">
        <span>Renseignez l'objectif à atteindre (facultatif)</span>
        <input v-model="quantityObjective" type="number" class="w-full border rounded">
      </div>
      <div class="flex flex-row gap-4 pb-4">
        <div class="flex flex-1 flex-col gap-y-3">
          <p>Début <span class="text-red">*</span></p>
          <DatePicker
            v-model:model-value="startDate"
            trigger-class="w-auto px-3"
            date-style="medium"
          />
        </div>
        <div class="flex flex-1 flex-col gap-y-3">
          <p>Fin <span class="text-red">*</span></p>
          <DatePicker
            v-model:model-value="endDate"
            :min-value="now(getLocalTimeZone())"
            trigger-class="w-auto px-3"
            date-style="medium"
          />
        </div>
      </div>
      <div class="pb-4">
        <p>Titre <span class="text-red">*</span></p>
        <input v-model="title" class="w-full border rounded">
      </div>
      <div class="pb-4">
        <p>Description <span class="text-red">*</span></p>
        <textarea v-model="description" rows="3" class="w-full border rounded" />
      </div>
      <div class="pb-4">
        <span>Visuels</span>
        <div class="flex flex-row justify-between items-center gap-4">
          <img
            v-if="(contestFile as FileManagerFile)?.url || (contestFile as ImageFile)?.renderPath"
            :src="(contestFile as FileManagerFile)?.url || (contestFile as ImageFile)?.renderPath"
            class="h-48 w-48"
          >
          <BaseButton primary prepend-icon="mdi-upload" @click="openFileUpload()">
            Ajouter une photo
          </BaseButton>
          <v-file-input
            v-show="false"
            id="input-file"
            accept="image/*"
            @update:model-value="handleFileUpload"
          />
        </div>
      </div>
    </div>
    <div class="flex w-full flex-row justify-end">
      <BaseButton
        primary
        density="comfortable"
        :loading="loading"
        :disabled="!isMandatoryFieldFilled"
        @click="onSubmit"
      >
        Valider le challenge
      </BaseButton>
    </div>
  </DialogContent>
</template>
